import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Phone, MessageCircle } from "lucide-react";
import { scrollTo } from "@/App";

const LINES = [
    { text: "WE DROP", accent: false },
    { text: "KERBS.", accent: true },
    { text: "YOU DRIVE", accent: false },
    { text: "SMOOTH.", accent: false },
];

const IMG = "https://images.unsplash.com/photo-1585314062340-f1a5a7c9328d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHx0YXJtYWMlMjB0ZXh0dXJlJTIwZGFya3xlbnwwfHx8fDE3ODYxODQ1Mzh8MA&ixlib=rb-4.1.0&q=85";

function MaskedLine({ text, accent, delay }) {
    return (
        <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
            <motion.span
                className={`block font-display uppercase leading-[0.85] tracking-tight text-[17vw] sm:text-[14vw] lg:text-[10.5vw] ${accent ? "text-amber" : "text-paper"}`}
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
            >
                {text}
            </motion.span>
        </span>
    );
}

export default function KineticHero() {
    const { scrollY } = useScroll();
    const yBg = useTransform(scrollY, [0, 800], [0, 180]);
    const opacity = useTransform(scrollY, [0, 500], [1, 0.2]);

    return (
        <section className="relative min-h-[100svh] flex flex-col justify-end overflow-hidden grain bg-tarmac" data-testid="hero-section">
            <motion.div className="absolute inset-0" style={{ y: yBg }}>
                <img src={IMG} alt="" className="w-full h-[120%] object-cover opacity-25" />
                <div className="absolute inset-0 bg-tarmac/60" />
            </motion.div>

            <motion.div style={{ opacity }} className="relative z-10 px-5 sm:px-8 pt-28 pb-10">
                <motion.p
                    className="font-mono text-xs tracking-[0.25em] uppercase text-amber mb-6"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2, duration: 0.8 }}
                >
                    [ Drop Kerbs UK — Dropped Kerb Specialists ]
                </motion.p>

                <h1 className="mb-8">
                    {LINES.map((l, i) => (
                        <MaskedLine key={l.text} {...l} delay={0.35 + i * 0.12} />
                    ))}
                </h1>

                <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-8 border-t border-ash pt-8">
                    <motion.p
                        className="max-w-md text-base lg:text-lg text-grit leading-relaxed"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.1, duration: 0.8 }}
                    >
                        Smooth, legal vehicle access to your drive, garage or site.
                        We handle everything — council planning permission to the final
                        roll of tarmac.
                    </motion.p>

                    <motion.div
                        className="flex flex-wrap gap-3"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.25, duration: 0.8 }}
                    >
                        <button
                            data-testid="hero-get-quote-btn"
                            onClick={() => scrollTo("#quote")}
                            className="group px-8 py-4 bg-amber text-tarmac font-mono text-xs tracking-[0.2em] uppercase font-bold hover:bg-paper transition-colors duration-200"
                        >
                            Get a free quote
                        </button>
                        <a
                            data-testid="hero-call-btn"
                            href="tel:+447971124650"
                            className="flex items-center gap-2 px-8 py-4 border border-ash text-paper font-mono text-xs tracking-[0.2em] uppercase hover:border-amber hover:text-amber transition-colors duration-200"
                        >
                            <Phone size={14} /> Call us
                        </a>
                        <a
                            data-testid="hero-whatsapp-btn"
                            href="https://wa.me/447971124650?text=Hi%20Drop%20Kerbs%20UK%2C%20I%27d%20like%20a%20quote%20for%20a%20dropped%20kerb."
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-8 py-4 border border-ash text-paper font-mono text-xs tracking-[0.2em] uppercase hover:border-amber hover:text-amber transition-colors duration-200"
                        >
                            <MessageCircle size={14} /> WhatsApp
                        </a>
                    </motion.div>
                </div>

                <motion.button
                    data-testid="hero-scroll-indicator"
                    onClick={() => scrollTo("#process")}
                    className="absolute bottom-10 right-5 sm:right-8 hidden md:flex flex-col items-center gap-2 text-grit hover:text-amber transition-colors duration-200"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.6 }}
                >
                    <span className="font-mono text-[10px] tracking-[0.3em] uppercase rotate-90 origin-center translate-y-[-8px]">Scroll</span>
                    <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
                        <ArrowDown size={16} />
                    </motion.span>
                </motion.button>
            </motion.div>
        </section>
    );
}
